import { useCallback, useEffect, useMemo, useState } from "react";
import useDefinedContext from "hooks/useDefinedContext";
import ChartTooltipManagerContext from "../contexts/ChartTooltipManagerContext";
import useChartDimensions from "features/Charting/hooks/useChartDimensions";
import { defaultStyles } from "@visx/tooltip";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import { useTheme } from "@mui/material";

interface UseChartTooltipManagerProps {
  x: number;
  y: number;
}

function useChartTooltipManager<T extends UseChartTooltipManagerProps>(props: T, tooltipWidth = 400) {
  const { x, y } = props;

  const theme = useTheme();
  const dimensions = useChartDimensions();
  const marginTop = dimensions.margin.top;
  const marginLeft = dimensions.margin.left;
  const marginRight = dimensions.margin.right;
  const containerWidth = dimensions.inner.width;
  const containerHeight = dimensions.inner.height;

  const { tooltipData, showTooltip, hideTooltip, TooltipInPortal } = useDefinedContext(ChartTooltipManagerContext);

  const open = tooltipData === props;

  const [clicked, setClicked] = useState(false);

  const [tooltipHeight, setTooltipHeight] = useState(0);
  const tooltipRef = useCallback((node: HTMLDivElement | null) => {
    if (node !== null) {
      setTooltipHeight(node.offsetHeight);
    }
  }, []);

  useEffect(() => {
    if (!open && clicked) setClicked(false);
  }, [clicked, open]);

  const handleMouseEnter = useCallback(() => {
    showTooltip({ tooltipData: props });
  }, [props, showTooltip]);

  const handleClick = useCallback(() => {
    setClicked(true);
    showTooltip({ tooltipData: props });
  }, [props, showTooltip]);

  const handleClose = useCallback(() => {
    hideTooltip();
    setClicked(false);
  }, [hideTooltip]);

  const handleMouseLeave = useCallback(() => {
    if (clicked) return;
    handleClose();
  }, [clicked, handleClose]);

  const onClickAway = useCallback(() => {
    hideTooltip();
    setClicked(false);
  }, [hideTooltip]);

  const isTooltipYWithinContainer = useMemo(() => {
    return y + marginTop + tooltipHeight < containerHeight;
  }, [y, marginTop, tooltipHeight, containerHeight]);

  const top = useMemo(() => {
    const proposedTop = y + marginTop;
    const tooltipBottomEdge = containerHeight - tooltipHeight + marginTop;
    return isTooltipYWithinContainer ? proposedTop : tooltipBottomEdge;
  }, [isTooltipYWithinContainer, y, marginTop, containerHeight, tooltipHeight]);

  const left = useMemo((): number => {
    const isTooltipWithinContainer = x + tooltipWidth < containerWidth;
    const proposedLeft = x + marginLeft;
    const tooltipRightEdge = containerWidth - tooltipWidth + marginLeft;
    const tooltipLeftEdge = proposedLeft - marginRight - tooltipWidth;

    return isTooltipWithinContainer ? proposedLeft : isTooltipYWithinContainer ? tooltipRightEdge : tooltipLeftEdge;
  }, [containerWidth, isTooltipYWithinContainer, marginLeft, marginRight, tooltipWidth, x]);

  const ChartTooltipPortal = useCallback(
    ({ children }: { children?: React.ReactNode }) =>
      open ? (
        <TooltipInPortal
          key={Math.random()}
          top={top}
          left={left}
          style={{
            ...defaultStyles,
            backgroundColor: "transparent",
            boxShadow: "none",
            padding: 0,
            pointerEvents: undefined,
            zIndex: theme.zIndex.tooltip + 1,
          }}
        >
          <ClickAwayListener onClickAway={onClickAway}>
            <div ref={tooltipRef}>{children}</div>
          </ClickAwayListener>
        </TooltipInPortal>
      ) : null,
    [open, TooltipInPortal, top, left, onClickAway, theme.zIndex.tooltip, tooltipRef]
  );

  return useMemo(
    () => ({
      open,
      clicked,
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
      onClose: handleClose,
      onClick: handleClick,
      ChartTooltipPortal,
    }),
    [ChartTooltipPortal, clicked, handleClick, handleClose, handleMouseEnter, handleMouseLeave, open]
  );
}
export default useChartTooltipManager;
